import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, User as UserIcon, ArrowRight, Eye, EyeOff, Check } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const PWD_RULES = [
  { test: (s) => s.length >= 8, label: "At least 8 characters" },
  { test: (s) => /[A-Z]/.test(s), label: "An uppercase letter" },
  { test: (s) => /[0-9]/.test(s), label: "A number" },
  { test: (s) => /[^A-Za-z0-9]/.test(s), label: "A symbol (!@#$…)" },
];

async function claimPendingReferral() {
  try {
    const pending = localStorage.getItem("pending_referral_code");
    if (!pending) return;
    try {
      await apiClient.post("/referral/claim", { code: pending });
    } catch (e) {
      // Non-fatal — referral may already be claimed, expired, or self-referral
      console.warn("Referral claim failed:", e?.response?.data?.detail || e?.message);
    }
    localStorage.removeItem("pending_referral_code");
  } catch (e) {
    console.warn("Could not read pending referral code:", e);
  }
}

const startGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthModal({ open, onClose, initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode);
  useEffect(() => { if (open) setMode(initialMode); }, [open, initialMode]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  // Prevent background scroll when modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="auth-modal"
        >
          <motion.div
            className="absolute inset-0 bg-[var(--ink)]/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="relative w-full max-w-md rounded-3xl bg-white shadow-xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]"
            initial={{ y: 24, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Top action bar — always visible. Holds "Sign in" toggle on signup view
             * so the link is reachable even on short viewports where the Emergent
             * badge would otherwise overlap the bottom of the modal. */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-[var(--line)] bg-white flex-shrink-0">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--ink-3)]">
                {mode === "signin" && "Sign in"}
                {mode === "signup" && "Sign up"}
                {mode === "forgot" && "Reset password"}
              </div>
              <div className="flex items-center gap-3">
                {mode === "signup" && (
                  <button
                    type="button"
                    onClick={() => setMode("signin")}
                    data-testid="auth-modal-header-signin"
                    className="text-xs font-semibold text-[var(--primary)] hover:underline min-h-[36px] px-2"
                  >
                    Sign in
                  </button>
                )}
                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={() => setMode("signup")}
                    data-testid="auth-modal-header-signup"
                    className="text-xs font-semibold text-[var(--primary)] hover:underline min-h-[36px] px-2"
                  >
                    Sign up
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full hover:bg-[var(--bg-2)] transition-colors text-[var(--ink-3)] hover:text-[var(--ink)]"
                  data-testid="auth-modal-close"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            {/* Scrollable body with bottom padding to clear the Emergent badge. */}
            <div
              className="overflow-y-auto p-6 sm:p-8"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
            >
              {mode === "signin" && <SignInPanel onSwitch={setMode} onClose={onClose} />}
              {mode === "signup" && <SignUpPanel onSwitch={setMode} onClose={onClose} />}
              {mode === "forgot" && <ForgotPanel onSwitch={setMode} />}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Header({ kicker, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="kicker text-[var(--primary)]">{kicker}</div>
      <h2 className="font-display text-3xl tracking-[-0.022em] mt-2 font-semibold">{title}</h2>
      {subtitle && <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function GoogleButton() {
  return (
    <button
      type="button"
      onClick={startGoogle}
      data-testid="auth-google-button"
      className="w-full inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-full border border-[var(--line-strong)] bg-white hover:bg-[var(--bg-2)] hover:border-[var(--ink)] transition-colors font-semibold text-[var(--ink)] text-sm"
    >
      <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84a10.13 10.13 0 0 1-4.39 6.65v5.52h7.1c4.16-3.83 6.57-9.47 6.57-16.18z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.32l-7.1-5.52c-1.97 1.32-4.49 2.1-7.46 2.1-5.74 0-10.6-3.87-12.34-9.07H4.34v5.7A21.99 21.99 0 0 0 24 46z"/><path fill="#FBBC05" d="M11.66 28.18A13.2 13.2 0 0 1 10.95 24c0-1.45.25-2.86.71-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.32-5.7z"/><path fill="#EA4335" d="M24 9.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 3.18 29.93 1 24 1 15.4 1 7.96 5.93 4.34 13.12l7.32 5.7C13.4 13.62 18.26 9.75 24 9.75z"/></svg>
      Continue with Google
    </button>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3 my-5">
      <div className="flex-1 h-px bg-[var(--line)]" />
      <span className="text-[0.65rem] uppercase tracking-[0.18em] text-[var(--ink-3)] font-semibold">Or with email</span>
      <div className="flex-1 h-px bg-[var(--line)]" />
    </div>
  );
}

function Field({ icon: Icon, label, children, testid }) {
  return (
    <label className="block mb-3.5" data-testid={testid}>
      <span className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block uppercase tracking-wider">{label}</span>
      <div className="relative">
        {Icon && <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />}
        <div className="[&>input]:pl-10">{children}</div>
      </div>
    </label>
  );
}

function PwdField({ value, onChange, testid, placeholder, autoComplete = "current-password" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="brut-input pl-10 pr-10"
        data-testid={testid}
      />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)] hover:text-[var(--ink)]" tabIndex={-1} aria-label={show ? "Hide password" : "Show password"}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

/* ───── SIGN IN ───── */
function SignInPanel({ onSwitch, onClose }) {
  const { setUser, fetchMe } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await apiClient.post("/auth/login", { email, password });
      await claimPendingReferral();
      const u = await fetchMe();
      setUser?.(u);
      toast.success(`Welcome back${u?.name ? `, ${u.name.split(" ")[0]}` : ""}!`);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not sign in. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="auth-signin-form">
      <Header kicker="Sign in" title="Welcome back." subtitle="Pick up your training where you left off." />
      <GoogleButton />
      <Divider />
      <Field icon={Mail} label="Email" testid="auth-signin-email-field">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className="brut-input pl-10" placeholder="you@aipromptdojo.com" data-testid="auth-signin-email" />
      </Field>
      <div className="mb-3.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-[var(--ink-2)] uppercase tracking-wider">Password</span>
          <button type="button" onClick={() => onSwitch("forgot")} className="text-xs font-semibold text-[var(--primary)] hover:underline" data-testid="auth-signin-forgot-link">
            Forgot?
          </button>
        </div>
        <PwdField value={password} onChange={setPassword} testid="auth-signin-password" placeholder="Your password" autoComplete="current-password" />
      </div>
      <button type="submit" disabled={busy} className="btn-duo w-full justify-center mt-2" data-testid="auth-signin-submit">
        {busy ? <span className="spinner" /> : <>Sign in <ArrowRight size={15} /></>}
      </button>
      <p className="text-center text-sm text-[var(--ink-2)] mt-5">
        New here?{" "}
        <button type="button" onClick={() => onSwitch("signup")} className="font-semibold text-[var(--primary)] hover:underline" data-testid="auth-switch-to-signup">Create account</button>
      </p>
    </form>
  );
}

/* ───── SIGN UP ───── */
function SignUpPanel({ onSwitch, onClose }) {
  const { setUser, fetchMe } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const allRulesPass = PWD_RULES.every((r) => r.test(password));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!allRulesPass) { toast.error("Please meet all password requirements."); return; }
    setBusy(true);
    try {
      await apiClient.post("/auth/register", { email, password, name });
      await claimPendingReferral();
      const u = await fetchMe();
      setUser?.(u);
      toast.success("Account created — let's get your White Belt 🥋");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create account. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="auth-signup-form">
      <Header kicker="Sign up" title="Save your progress." subtitle="Create your account to continue training." />
      <GoogleButton />
      <Divider />
      <Field icon={UserIcon} label="Display name" testid="auth-signup-name-field">
        <input type="text" required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className="brut-input pl-10" placeholder="What should we call you?" data-testid="auth-signup-name" />
      </Field>
      <Field icon={Mail} label="Email" testid="auth-signup-email-field">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className="brut-input pl-10" placeholder="you@aipromptdojo.com" data-testid="auth-signup-email" />
      </Field>
      <div className="mb-2">
        <span className="text-xs font-semibold text-[var(--ink-2)] uppercase tracking-wider mb-1.5 block">Password</span>
        <PwdField value={password} onChange={setPassword} testid="auth-signup-password" placeholder="Create a strong password" autoComplete="new-password" />
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 mb-4" data-testid="auth-signup-rules">
        {PWD_RULES.map((r) => {
          const pass = r.test(password);
          return (
            <li key={r.label} className="flex items-center gap-1.5 text-[0.72rem]" style={{ color: pass ? "var(--secondary-dark)" : "var(--ink-3)" }}>
              <Check size={12} strokeWidth={pass ? 3 : 2} style={{ opacity: pass ? 1 : 0.4 }} />
              {r.label}
            </li>
          );
        })}
      </ul>
      <button type="submit" disabled={busy || !allRulesPass} className="btn-duo w-full justify-center" data-testid="auth-signup-submit">
        {busy ? <span className="spinner" /> : <>Create account <ArrowRight size={15} /></>}
      </button>
      <p className="text-[0.7rem] text-[var(--ink-3)] mt-3 text-center leading-relaxed">
        By creating an account, you agree to our{" "}
        <a href="/terms" target="_blank" rel="noreferrer" className="text-[var(--ink-2)] hover:underline">Terms</a> and{" "}
        <a href="/privacy" target="_blank" rel="noreferrer" className="text-[var(--ink-2)] hover:underline">Privacy Policy</a>.
      </p>
      <p className="text-center text-sm text-[var(--ink-2)] mt-4">
        Already have an account?{" "}
        <button type="button" onClick={() => onSwitch("signin")} className="font-semibold text-[var(--primary)] hover:underline" data-testid="auth-switch-to-signin">Sign in</button>
      </p>
    </form>
  );
}

/* ───── FORGOT PASSWORD ───── */
function ForgotPanel({ onSwitch }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await apiClient.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send reset link.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="auth-forgot-sent">
        <Header kicker="Check your inbox" title="Reset link sent." subtitle="If that email is registered, you'll receive a reset link in the next minute. The link expires in 1 hour." />
        <button onClick={() => onSwitch("signin")} className="btn-ghost w-full justify-center" data-testid="auth-forgot-back">
          Back to sign in
        </button>
        <p className="text-xs text-[var(--ink-3)] text-center mt-4">Didn't get it? Check spam, or contact support@aipromptdojo.com</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="auth-forgot-form">
      <Header kicker="Forgot password" title="Reset your password." subtitle="Enter your email and we'll send you a one-time link to reset it." />
      <Field icon={Mail} label="Email" testid="auth-forgot-email-field">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className="brut-input pl-10" placeholder="you@aipromptdojo.com" data-testid="auth-forgot-email" />
      </Field>
      <button type="submit" disabled={busy} className="btn-duo w-full justify-center mt-2" data-testid="auth-forgot-submit">
        {busy ? <span className="spinner" /> : <>Send reset link <ArrowRight size={15} /></>}
      </button>
      <p className="text-center text-sm text-[var(--ink-2)] mt-5">
        <button type="button" onClick={() => onSwitch("signin")} className="font-semibold text-[var(--primary)] hover:underline">← Back to sign in</button>
      </p>
    </form>
  );
}
